import React from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import {
  applyCepMask,
  applyCnpjMask,
  applyCpfMask,
  applyDataNascimentoMask,
  applyIdadeMask,
} from '../../../utils/fieldMasks';

export type CheckoutCustomField = {
  _id: string;
  fieldName: string;
  fieldType:
    | 'text'
    | 'select'
    | 'checkbox'
    | 'number'
    | 'label'
    | 'cpf'
    | 'cnpj'
    | 'idade'
    | 'cep'
    | 'dataNascimento';
  isRequired: boolean;
  options?: string[];
  order?: number;
};

type Props = {
  fields: CheckoutCustomField[];
  control: Control<any>;
  namePrefix: string;
  errors?: FieldErrors<Record<string, any>>;
};

const RegistrationCustomFieldInputs: React.FC<Props> = ({
  fields,
  control,
  namePrefix,
  errors,
}) => {
  const sorted = [...fields].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return (
    <>
      {sorted.map((field) => {
        const fieldName = `${namePrefix}.${field._id}`;
        const fieldError = errors?.[field._id]?.message as string | undefined;
        const label = field.fieldName + (field.isRequired ? ' *' : '');

        return (
          <Grid item xs={12} key={field._id}>
            <FormControl fullWidth error={!!fieldError}>
              {field.fieldType === 'checkbox' ? (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
                    {label}
                  </Typography>
                  <Controller
                    name={fieldName}
                    control={control}
                    defaultValue={[]}
                    render={({ field: checkboxField }) => (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {field.options?.map((option) => {
                          const selected =
                            Array.isArray(checkboxField.value) && checkboxField.value.includes(option);
                          return (
                            <Paper
                              key={`${field._id}-${option}`}
                              elevation={0}
                              sx={{
                                p: 1.5,
                                border: selected ? '2px solid #1976d2' : '1px solid #e0e0e0',
                                cursor: 'pointer',
                              }}
                              onClick={() => {
                                const current = Array.isArray(checkboxField.value)
                                  ? checkboxField.value
                                  : [];
                                checkboxField.onChange(
                                  current.includes(option)
                                    ? current.filter((val: string) => val !== option)
                                    : [...current, option]
                                );
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Checkbox checked={!!selected} />
                                <Typography>{option}</Typography>
                              </Box>
                            </Paper>
                          );
                        })}
                      </Box>
                    )}
                  />
                </Box>
              ) : field.fieldType === 'select' ? (
                <>
                  <InputLabel id={`${fieldName}-label`}>{label}</InputLabel>
                  <Controller
                    name={fieldName}
                    control={control}
                    defaultValue=""
                    render={({ field: selectField }) => (
                      <Select {...selectField} labelId={`${fieldName}-label`} label={label}>
                        {field.options?.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </>
              ) : field.fieldType === 'label' ? (
                <Typography color="primary" sx={{ py: 1, fontWeight: 500 }}>
                  {field.fieldName}
                </Typography>
              ) : field.fieldType === 'cpf' ? (
                <Controller
                  name={fieldName}
                  control={control}
                  defaultValue=""
                  render={({ field: inputField }) => (
                    <TextField
                      {...inputField}
                      label={label}
                      placeholder="000.000.000-00"
                      fullWidth
                      error={!!fieldError}
                      helperText={fieldError || 'Digite apenas números'}
                      onChange={(e) => inputField.onChange(applyCpfMask(e.target.value))}
                      inputProps={{ maxLength: 14 }}
                    />
                  )}
                />
              ) : field.fieldType === 'cnpj' ? (
                <Controller
                  name={fieldName}
                  control={control}
                  defaultValue=""
                  render={({ field: inputField }) => (
                    <TextField
                      {...inputField}
                      label={label}
                      placeholder="00.000.000/0000-00"
                      fullWidth
                      error={!!fieldError}
                      helperText={fieldError}
                      onChange={(e) => inputField.onChange(applyCnpjMask(e.target.value))}
                      inputProps={{ maxLength: 18 }}
                    />
                  )}
                />
              ) : field.fieldType === 'cep' ? (
                <Controller
                  name={fieldName}
                  control={control}
                  defaultValue=""
                  render={({ field: inputField }) => (
                    <TextField
                      {...inputField}
                      label={label}
                      placeholder="00000-000"
                      fullWidth
                      error={!!fieldError}
                      helperText={fieldError}
                      onChange={(e) => inputField.onChange(applyCepMask(e.target.value))}
                      inputProps={{ maxLength: 9 }}
                    />
                  )}
                />
              ) : field.fieldType === 'idade' ? (
                <Controller
                  name={fieldName}
                  control={control}
                  defaultValue=""
                  render={({ field: inputField }) => (
                    <TextField
                      {...inputField}
                      label={label}
                      fullWidth
                      error={!!fieldError}
                      helperText={fieldError}
                      onChange={(e) => inputField.onChange(applyIdadeMask(e.target.value))}
                      inputProps={{ maxLength: 3 }}
                    />
                  )}
                />
              ) : field.fieldType === 'dataNascimento' ? (
                <Controller
                  name={fieldName}
                  control={control}
                  defaultValue=""
                  render={({ field: inputField }) => (
                    <TextField
                      {...inputField}
                      label={label}
                      placeholder="DD/MM/AAAA"
                      fullWidth
                      error={!!fieldError}
                      helperText={fieldError}
                      onChange={(e) => inputField.onChange(applyDataNascimentoMask(e.target.value))}
                      inputProps={{ maxLength: 10 }}
                    />
                  )}
                />
              ) : (
                <Controller
                  name={fieldName}
                  control={control}
                  defaultValue=""
                  render={({ field: inputField }) => (
                    <TextField
                      {...inputField}
                      label={label}
                      type={field.fieldType === 'number' ? 'number' : 'text'}
                      fullWidth
                      error={!!fieldError}
                      helperText={fieldError}
                    />
                  )}
                />
              )}
              {fieldError && field.fieldType === 'checkbox' ? (
                <FormHelperText>{fieldError}</FormHelperText>
              ) : null}
            </FormControl>
          </Grid>
        );
      })}
    </>
  );
};

export default RegistrationCustomFieldInputs;
